import { DateTimeValueSchemas } from "@validereinc/utilities";
import { z } from "zod";
import {
  createGetListRequestSchema,
  createGetOneRequestSchema,
  DomainModelMetaExtendedSchema,
  DomainModelMetaSchema,
  DomainModelReferenceSchema,
  DomainModelSchema,
  IdentifierSchema,
} from "../util/index.ts";
import { CustomAttributeRecordSchema } from "./CustomAttributeSchemas.ts";

export const EquipmentStatus = {
  ACTIVE: "active",
  INACTIVE: "inactive",
  DEPRECATED: "deprecated",
} as const;
export type EquipmentStatusType =
  (typeof EquipmentStatus)[keyof typeof EquipmentStatus];

export const EquipmentTypeSchema = z.object({
  id: z.string(),
  name: z.string(),
});
export type EquipmentTypeType = z.infer<typeof EquipmentTypeSchema>;

export const EquipmentOperatingConditionSchema = z.intersection(
  z.object({
    year_month: DateTimeValueSchemas.dateYearMonth,
  }),
  z.record(z.number())
);

export const EquipmentSchema = z
  .object({
    name: z.string(),
    status: z.enum([
      EquipmentStatus.ACTIVE,
      EquipmentStatus.INACTIVE,
      EquipmentStatus.DEPRECATED,
    ]),
    latitude: z.number().lte(90).gte(-90),
    longitude: z.number().lte(180).gte(-180),
    effective_date: DateTimeValueSchemas.dateTime,
    facility_id: IdentifierSchema,
    facility: DomainModelReferenceSchema,
    type_id: z.string(),
    type: EquipmentTypeSchema,
    custom_attributes: CustomAttributeRecordSchema,
  })
  .merge(DomainModelSchema)
  .merge(DomainModelMetaSchema)
  .merge(DomainModelMetaExtendedSchema)
  .describe("Represents an Equipment asset in the Node API");
export type EquipmentType = z.infer<typeof EquipmentSchema>;

export const EquipmentGetListRequestSchema = createGetListRequestSchema(
  EquipmentSchema.pick({
    id: true,
    name: true,
    status: true,
    custom_attributes: true,
  })
    .merge(
      z.object({
        "type.id": z.string(),
        "type.name": z.string(),
        "event.id": z.string(),
        "facility.id": z.string(),
        id: z.string().uuid().or(z.array(z.string().uuid())),
      })
    )
    .partial(),
  z.object({
    period: DateTimeValueSchemas.dateYearMonth,
    isExcelFile: z.boolean(),
  })
);
export type EquipmentGetListRequestType = z.infer<
  typeof EquipmentGetListRequestSchema
>;

export const EquipmentGetOneRequestSchema = createGetOneRequestSchema(
  z.object({
    period: DateTimeValueSchemas.dateYearMonth,
  })
);
export type EquipmentGetOneRequestType = z.infer<
  typeof EquipmentGetOneRequestSchema
>;
